import { HttpHeaders, HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { applicationConfiguration, AppConfig } from '../interceptor/app.config';
import { secureStorage } from './secure-storage.service';

export interface PaymentRequest {
  order_number?: string;
  invoice_id?: number;
  payment_flow?: string;
  is_retry_payment?: boolean;
  user_id?: number;
  transport_details?: Record<string, unknown>;
}

export interface PaymentResponse {
  success: boolean;
  data?: any;
  message?: string;
  error?: string;
}

export interface CCAvenuePaymentData {
  access_code: string;
  merchant_id: string;
  order_id: string;
  amount: string;
  currency: string;
  redirect_url: string;
  cancel_url: string;
  language: string;
  billing_name: string;
  billing_address: string;
  billing_city: string;
  billing_state: string;
  billing_zip: string;
  billing_country: string;
  billing_tel: string;
  billing_email: string;
  delivery_name: string;
  delivery_address: string;
  delivery_city: string;
  delivery_state: string;
  delivery_zip: string;
  delivery_country: string;
  delivery_tel: string;
  merchant_param1: string;
  merchant_param2: string;
  merchant_param3: string;
  merchant_param4: string;
  merchant_param5: string;
  promo_code: string;
  customer_identifier: string;
}

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  protected server = this.appConfig.apiUrl + 'api/eol/v1/pv/';
  protected server2 = this.appConfig.apiUrl + 'api/eol/';

  private paymentStatusSubject = new BehaviorSubject<any>(null);
  currentPaymentStatus = this.paymentStatusSubject.asObservable();

  private paymentDataSubject = new BehaviorSubject<any>(null);
  currentPaymentData = this.paymentDataSubject.asObservable();

  readonly options = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  constructor(
    private http: HttpClient,
    private localstorage: secureStorage,
    @Inject(applicationConfiguration) private appConfig: AppConfig,
  ) {}

  /**
   * Get encrypted payment data from CCAvenue
   * @param payload Payment request payload containing order_number
   * @returns Observable with encrypted payment data
   */
  getEncryptedPaymentData(
    payload: PaymentRequest,
  ): Observable<PaymentResponse> {
    return this.http.post<PaymentResponse>(
      `${this.server}order/cc_get_enc/`,
      payload,
      this.options,
    );
  }

  /**
   * Process payment response from CCAvenue
   * @param responseData Payment response data from CCAvenue
   * @returns Observable with payment processing result
  //  */
  // processPaymentResponse(responseData: any): Observable<PaymentResponse> {
  //   return this.http.post<PaymentResponse>(`${this.server}order/process_payment_response/`, responseData, this.options);
  // }

  // /**
  //  * Get payment status for an order
  //  * @param orderNumber Order number to check payment status
  //  * @returns Observable with payment status
  //  */
  // getPaymentStatus(orderNumber: string): Observable<PaymentResponse> {
  //   const payload = { order_number: orderNumber };
  //   return this.http.post<PaymentResponse>(`${this.server}order/get_payment_status/`, payload, this.options);
  // }

  /**
   * Cancel payment for an order
   * @param orderNumber Order number to cancel payment
   * @returns Observable with cancellation result
   */
  // cancelPayment(orderNumber: string): Observable<PaymentResponse> {
  //   const payload = { order_number: orderNumber };
  //   return this.http.post<PaymentResponse>(`${this.server}order/cancel_payment/`, payload, this.options);
  // }

  /**
   * Refund payment for an order
   * @param payload Refund request payload
   * @returns Observable with refund result
   */
  refundPayment(payload: any): Observable<PaymentResponse> {
    return this.http.post<PaymentResponse>(
      `${this.server}order/refund_payment/`,
      payload,
      this.options,
    );
  }

  /**
   * Update payment status in local storage
   * @param status Payment status to update
   */
  updatePaymentStatus(status: any): void {
    this.paymentStatusSubject.next(status);
  }

  /**
   * Update payment data in local storage
   * @param data Payment data to update
   */
  updatePaymentData(data: any): void {
    this.paymentDataSubject.next(data);
  }

  /**
   * Get current payment status from local storage
   * @returns Current payment status
   */
  getCurrentPaymentStatus(): any {
    return this.paymentStatusSubject.value;
  }

  /**
   * Get current payment data from local storage
   * @returns Current payment data
   */
  getCurrentPaymentData(): any {
    return this.paymentDataSubject.value;
  }

  /**
   * Clear payment data from local storage
   */
  clearPaymentData(): void {
    this.paymentStatusSubject.next(null);
    this.paymentDataSubject.next(null);
  }

  /**
   * Validate payment request payload
   * @param payload Payment request payload
   * @returns True if valid, false otherwise
   */
  validatePaymentRequest(payload: PaymentRequest): boolean {
    if (!payload) return false;
    if (payload.invoice_id) return true;
    if (!payload.order_number) return false;
    return payload.order_number.trim().length > 0;
  }

  /**
   * Format order number for display
   * @param orderNumber Raw order number
   * @returns Formatted order number
   */
  formatOrderNumber(orderNumber: string): string {
    if (!orderNumber) return '';
    return orderNumber.replace(/-/g, ' ').toUpperCase();
  }

  /**
   * Check if payment is in progress
   * @returns True if payment is in progress
   */
  isPaymentInProgress(): boolean {
    const status = this.getCurrentPaymentStatus();
    return status && status.status === 'in_progress';
  }

  /**
   * Check if payment was successful
   * @returns True if payment was successful
   */
  isPaymentSuccessful(): boolean {
    const status = this.getCurrentPaymentStatus();
    return status && status.status === 'success';
  }

  /**
   * Check if payment failed
   * @returns True if payment failed
   */
  isPaymentFailed(): boolean {
    const status = this.getCurrentPaymentStatus();
    return status && status.status === 'failed';
  }
}
